/** The Tools overlay — every action in the product, on one screen.
 *
 * Two rules it exists to keep, and keeps structurally:
 *
 *   COMPLETE   — it is GENERATED from `features/tool-registry`, so it cannot be
 *                out of date and a tool cannot hide behind an overflow menu.
 *   CONTAINED  — a tool declares one `group`, so it cannot appear in two homes.
 *
 * The look is iOS Settings: the app stays where it was and is blurred through,
 * and the tools sit in frosted grouped rectangles over it.
 *
 * The pen, highlighter and eraser are deliberately NOT here. They were, briefly,
 * on the theory that people look for writing tools first — but the annotation
 * toolbar is already on screen and unmissable whenever writing is possible, so
 * putting them here gave them a second home and broke the containment rule this
 * overlay exists to keep. A tool belongs in exactly one place.
 */
import { Search } from "lucide-react";
import * as React from "react";

import {
  Dialog,
  DialogPopup,
  DialogPortal,
  DialogTitle,
} from "@/components/ui/overlays";
import { cn } from "@/lib/utils";
import {
  TOOL_GROUPS,
  searchTools,
  toolsInGroup,
  type ToolContext,
  type ToolDescriptor,
  type ToolGroup,
} from "@/features/tool-registry";
import "@/features/tools";
import { useIsCompact } from "@/hooks/use-media-query";
import { useSheetDrag } from "@/components/appshell/use-sheet-drag";
import { useToolsOverlay } from "@/stores/tools-overlay";

/** Per-group tile tint. Kept to the theme's own accents so the grid reads as
 *  one system rather than a paint box. */
const GROUP_TINT: Record<ToolGroup, string> = {
  create: "bg-viridian/15 text-viridian ring-1 ring-inset ring-viridian/25",
  find: "bg-skyb/15 text-skyb ring-1 ring-inset ring-skyb/25",
  graph: "bg-orchid/15 text-orchid ring-1 ring-inset ring-orchid/25",
  data: "bg-amber-400/15 text-amber-400 ring-1 ring-inset ring-amber-400/25",
  workspace: "bg-muted/60 text-foreground/80 ring-1 ring-inset ring-border",
};

function ToolTile({
  tool,
  ctx,
  size = "md",
  onRun,
}: {
  tool: ToolDescriptor;
  ctx: ToolContext;
  size?: "md" | "lg";
  onRun: (tool: ToolDescriptor) => void;
}) {
  const active = tool.isActive?.(ctx) ?? false;
  // A 54px square is a comfortable mouse target and a mean one for a thumb.
  // iOS puts a home-screen icon at 60pt and Apple's own minimum is 44pt for the
  // TAP AREA — the label below adds to that, but the icon is what the eye aims
  // at, so it grows on touch rather than relying on the text to be hit.
  const box =
    size === "lg"
      ? "h-[68px] w-[68px] rounded-[20px]"
      : "h-[54px] w-[54px] rounded-[16px] max-md:h-[62px] max-md:w-[62px] max-md:rounded-[18px]";
  const glyph = size === "lg" ? "h-7 w-7" : "h-[22px] w-[22px] max-md:h-6 max-md:w-6";

  return (
    <button
      type="button"
      onClick={() => onRun(tool)}
      title={tool.hint ?? tool.label}
      aria-pressed={tool.isActive ? active : undefined}
      className="group flex flex-col items-center gap-2 rounded-xl p-1 outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <span
        className={cn(
          "relative flex items-center justify-center transition-transform duration-150 group-hover:scale-[1.06] group-active:scale-[0.96]",
          box,
          active
            ? "bg-primary text-primary-foreground shadow-[0_6px_18px_hsl(var(--primary)/0.45)] ring-0"
            : GROUP_TINT[tool.group],
        )}
      >
        <tool.icon className={glyph} strokeWidth={1.7} aria-hidden="true" />
        {/* A key hint is a promise a touch device cannot keep, so it is not
            printed on one. `max-md:hidden` rather than a prop: the tile is
            rendered by the same component on both, and this is a purely
            presentational difference. */}
        {tool.shortcut && (
          <span className="absolute bottom-1 right-1.5 font-mono text-[9px] leading-none text-current opacity-45 max-md:hidden">
            {tool.shortcut}
          </span>
        )}
      </span>
      <span
        className={cn(
          "max-w-[76px] text-center text-[11px] leading-tight",
          active ? "text-foreground" : "text-muted-foreground group-hover:text-foreground",
        )}
      >
        {tool.label}
      </span>
    </button>
  );
}

/** One frosted rectangle. iOS Settings groups a handful of rows in a card; this
 *  groups a handful of tiles the same way. */
function GroupCard({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section
      className={cn(
        "rounded-2xl border border-border/60 bg-card/55 p-4 backdrop-blur-md",
        className,
      )}
    >
      <h3 className="mb-3 font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
        {label}
      </h3>
      {children}
    </section>
  );
}

export function ToolsOverlay({ ctx }: { ctx: ToolContext }) {
  const open = useToolsOverlay((s) => s.open);
  const closeTools = useToolsOverlay((s) => s.closeTools);
  const compact = useIsCompact();
  const [query, setQuery] = React.useState("");
  // The grabber is not decoration: the sheet really does follow the finger and
  // dismiss when thrown. See use-sheet-drag.
  const sheet = useSheetDrag(compact && open, closeTools);

  React.useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const run = React.useCallback(
    (tool: ToolDescriptor) => {
      tool.run(ctx);
      if (!tool.keepOpen) closeTools();
    },
    [ctx, closeTools],
  );

  const hits = query ? searchTools(query, ctx) : [];

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? undefined : closeTools())}>
      <DialogPortal>
        {/* ONE scrim, and a light one: the whole app must stay legible through
            the blur. That is the iOS Settings feel — you have not gone
            anywhere, the thing you were doing is right there behind the glass.
            The house DialogBackdrop is bg-black/60; stacking this on top of it
            turned the page behind into a black wall, so it is deliberately not
            rendered here. */}
        <DialogPopup
          ref={sheet.sheetRef}
          // Space opens it, so Space closes it. The global handler cannot do the
          // closing half: this popup autofocuses its search box, and that handler
          // (rightly) ignores keys typed into a field. With the box empty there
          // is nothing to type a space into, so Space is the close key; once you
          // have typed a query it goes back to being a space.
          onKeyDown={(e) => {
            if (e.key === " " && !query) {
              e.preventDefault();
              closeTools();
            }
          }}
          className={cn(
            "fixed z-50 overflow-y-auto bg-background/35 backdrop-blur-[26px] backdrop-saturate-[1.35]",
            // A SHEET on touch, a full surface on desktop. Covering a phone
            // edge to edge hides the thing you were doing and leaves no way
            // back but a button; a sheet keeps the workspace visible above it
            // and can be dismissed by tapping what it did not cover, which is
            // how every modal on the platform behaves.
            compact
              ? "inset-x-0 bottom-0 top-[max(10vh,72px)] rounded-t-[28px] border-t border-white/10 shadow-[0_-24px_60px_-12px_rgba(0,0,0,0.75)] data-[open]:animate-sheet-up"
              : "inset-0 data-[open]:animate-rise-in",
          )}
        >
          <div
            className={cn(
              "mx-auto flex min-h-full w-full max-w-[1180px] flex-col gap-4 px-6 py-7 sm:px-10",
              compact && "gap-3 px-4 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-2",
            )}
          >
            {compact && (
              // The grabber, and the drag target that makes it true. A generous
              // invisible band around the pill, because a 4px bar is not a
              // thumb-sized handle however clearly it reads.
              <div
                {...sheet.handleProps}
                className="-mx-4 -mt-2 flex h-7 shrink-0 cursor-grab items-center justify-center active:cursor-grabbing"
              >
                <span aria-hidden className="h-1 w-10 rounded-full bg-foreground/25" />
              </div>
            )}

            <header
              {...(compact ? sheet.handleProps : {})}
              className={cn("flex items-center gap-4", compact && "gap-2")}
            >
              <DialogTitle
                className={cn(
                  "font-display font-semibold text-foreground",
                  compact ? "text-xl" : "text-2xl",
                )}
              >
                Tools
              </DialogTitle>
              {!compact && (
                <div className="relative flex-1">
                  <label htmlFor="tools-search" className="sr-only">
                    Search tools
                  </label>
                  <Search
                    className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                    aria-hidden="true"
                  />
                  <input
                    id="tools-search"
                    autoFocus
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search every tool…"
                    className="h-10 w-full rounded-xl border border-border/70 bg-card/70 pl-10 pr-3 text-[13.5px] text-foreground backdrop-blur-md outline-none placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring"
                  />
                </div>
              )}
              <button
                type="button"
                onClick={closeTools}
                className={cn(
                  "flex items-center gap-2 rounded-xl border border-border/70 bg-card/70 backdrop-blur-md transition-colors hover:text-foreground",
                  compact
                    ? "ml-auto h-9 px-4 text-[13px] font-medium text-foreground/80"
                    : "h-10 px-3 text-[12.5px] text-muted-foreground",
                )}
              >
                {compact ? "Done" : "Close"}
                {!compact && <span className="font-mono text-[10.5px]">Esc</span>}
              </button>
            </header>

            {/* On touch the search sits BELOW the title, full width, and never
                autofocuses: a keyboard thrown up on open would cover the grid
                the person came here to look at. They can tap it if they want
                to type. */}
            {compact && (
              <div className="relative shrink-0">
                <label htmlFor="tools-search" className="sr-only">
                  Search tools
                </label>
                <Search
                  className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  aria-hidden="true"
                />
                <input
                  id="tools-search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search every tool…"
                  className="h-11 w-full rounded-xl border border-border/70 bg-card/70 pl-10 pr-3 text-[15px] text-foreground backdrop-blur-md outline-none placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring"
                />
              </div>
            )}

            {query ? (
              <GroupCard label={`${hits.length} match${hits.length === 1 ? "" : "es"}`}>
                {hits.length ? (
                  <div className="grid grid-cols-[repeat(auto-fill,minmax(88px,1fr))] gap-x-2 gap-y-4">
                    {hits.map((t) => (
                      <ToolTile key={t.id} tool={t} ctx={ctx} onRun={run} />
                    ))}
                  </div>
                ) : (
                  <p className="py-6 text-center text-[13px] text-muted-foreground">
                    Nothing matches “{query}”.
                  </p>
                )}
              </GroupCard>
            ) : (
              <>
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                  {TOOL_GROUPS.map((g) => {
                    const tools = toolsInGroup(g.id, ctx);
                    if (!tools.length) return null;
                    return (
                      <GroupCard key={g.id} label={g.label}>
                        <div className="grid grid-cols-[repeat(auto-fill,minmax(80px,1fr))] gap-x-2 gap-y-4">
                          {tools.map((t) => (
                            <ToolTile key={t.id} tool={t} ctx={ctx} onRun={run} />
                          ))}
                        </div>
                      </GroupCard>
                    );
                  })}
                </div>
              </>
            )}

            {!compact && (
              <p className="pb-2 text-center text-[11.5px] text-muted-foreground">
                Every action in the product, generated from one registry — so nothing here can be
                out of date, and nothing is hiding behind a menu.
              </p>
            )}
          </div>
        </DialogPopup>
      </DialogPortal>
    </Dialog>
  );
}

/** The rail button that summons it. */
export function ToolsButton({ className }: { className?: string }) {
  const openTools = useToolsOverlay((s) => s.openTools);
  return (
    <button
      type="button"
      onClick={() => openTools()}
      className={cn(
        "flex h-[34px] w-full items-center justify-center gap-2 rounded-lg bg-primary text-[13px] font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90",
        className,
      )}
    >
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" aria-hidden="true">
        <rect x="3" y="3" width="7" height="7" rx="2" />
        <rect x="14" y="3" width="7" height="7" rx="2" />
        <rect x="3" y="14" width="7" height="7" rx="2" />
        <rect x="14" y="14" width="7" height="7" rx="2" />
      </svg>
      Tools
      <span className="font-mono text-[10px] opacity-70">Space</span>
    </button>
  );
}
